/**
 * SecurityDashboard — full-page security scan viewer for admin.optiprime.ai/security
 *
 * Left: scan history list (date, status badge, severity counts)
 * Right: selected scan rendered as the same HTML email layout, plus:
 *   - Findings table with filters, status actions (acknowledge / resolve / false_positive)
 *   - Security Coordinator chat (Qwen 32B)
 *   - On-demand scan trigger
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './SecurityDashboard.css';

const SEVERITY_COLOR = {
  critical: '#dc2626',
  high:     '#ea580c',
  medium:   '#d97706',
  low:      '#65a30d',
  info:     '#0891b2',
};


const SUB_AGENT_LABELS = {
  dependency_auditor: '📦 Dependency Auditor',
  secret_scanner:     '🔑 Secret Scanner',
  auth_sentinel:      '🔐 Auth Sentinel',
  endpoint_monitor:   '🌐 Endpoint Monitor',
  config_compliance:  '⚙️ Config Compliance',
  dast_scanner:       '🌍 DAST Scanner',
  python_dependency_auditor: '🐍 Python Dependencies',
  tls_monitor:        '🔒 TLS Certificates',
  cloudflare_edge:    '☁️ Cloudflare Edge',
};

const STATUS_LABELS = {
  open:          'Open',
  acknowledged:  'Acknowledged',
  escalated:     'Escalated',
  resolved:      'Resolved',
  false_positive:'False Positive',
  accepted_risk: 'Accepted Risk',
};

const STATUS_COLORS = {
  open:          '#dc2626',
  acknowledged:  '#d97706',
  escalated:     '#ea580c',
  resolved:      '#16a34a',
  false_positive:'#6b7280',
  accepted_risk: '#64748b',
};

const ACCEPTED_COLOR = '#64748b';

const REASON_LABELS = {
  no_fix:      'No patched version exists',
  dev_only:    'Only in development tooling',
  unreachable: 'Vulnerable code path not reachable',
  other:       'Other (explain)',
};

const ACCEPTANCE_STATE_LABELS = {
  active:        'Active',
  fix_available: 'Fix available — reopened',
  expired:       'Review overdue — reopened',
  revoked:       'Revoked',
  closed:        'Closed — advisory withdrawn',
};

/** Root GHSA ids for a dependency finding (set by OptiGuard; older rows fall back to via urls). */
function advisoryIdsOf(f) {
  const raw = f.raw_details || {};
  if (Array.isArray(raw.advisory_ids) && raw.advisory_ids.length) return raw.advisory_ids;
  const via = Array.isArray(raw.advisory?.via) ? raw.advisory.via : [];
  return via
    .filter(v => v && typeof v === 'object' && /GHSA-/i.test(v.url || ''))
    .map(v => (v.url.match(/GHSA(?:-[0-9a-z]{4}){3}/i) || [])[0])
    .filter(Boolean);
}

/**
 * Covered by an accepted risk in this run's snapshot. Runs from before accepted risk existed have
 * a NULL disposition, so they keep showing what was actionable at the time.
 */
function isAcceptedInRun(f) {
  if (f.disposition !== undefined) return f.disposition === 'accepted_risk';
  return f.status === 'accepted_risk';
}

const ACTIVE_STATUSES = new Set(['open', 'acknowledged', 'escalated', 'accepted_risk']);

/** Needs attention in this run: not covered by accepted risk and not resolved / false positive. */
function isActiveInRun(f) {
  return !isAcceptedInRun(f) && ACTIVE_STATUSES.has(f.status);
}

function addDaysIso(days) {
  const d = new Date(Date.now() + days * 86400000);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/Vancouver' });
}

/** Normalize counts from GET /api/security/findings (handles string values and missing total). */
function normalizeApiCounts(c) {
  if (!c || typeof c !== 'object') return null;
  const critical = Number(c.critical) || 0;
  const high = Number(c.high) || 0;
  const medium = Number(c.medium) || 0;
  const low = Number(c.low) || 0;
  const info = Number(c.info) || 0;
  const t = Number(c.total);
  const total = Number.isFinite(t) ? t : critical + high + medium + low + info;
  return { critical, high, medium, low, info, total };
}

/**
 * Same fields the scan-history sidebar uses for badges. Prefers actionable_* (accepted
 * risk left out), then detected_*, then legacy backlog counts.
 */
function detectionCountsFromRunRow(run) {
  if (!run) return null;
  if (run.actionable_total != null) {
    return {
      critical: Number(run.actionable_critical) || 0,
      high: Number(run.actionable_high) || 0,
      medium: Number(run.actionable_medium) || 0,
      low: Number(run.actionable_low) || 0,
      info: Number(run.actionable_info) || 0,
      total: Number(run.actionable_total) || 0,
    };
  }
  const useDetected = run.detected_total != null;
  const critical = Number(useDetected ? run.detected_critical : run.critical_count) || 0;
  const high = Number(useDetected ? run.detected_high : run.high_count) || 0;
  const medium = Number(useDetected ? run.detected_medium : run.medium_count) || 0;
  const low = Number(useDetected ? run.detected_low : run.low_count) || 0;
  const info = Number(useDetected ? run.detected_info : run.info_count) || 0;
  const totalRaw = Number(useDetected ? run.detected_total : run.total_findings);
  const total = Number.isFinite(totalRaw) ? totalRaw : critical + high + medium + low + info;
  return { critical, high, medium, low, info, total };
}

/** Group scans by calendar month (newest months first; runs within month newest first). */
function groupRunsByMonth(runs) {
  const byKey = new Map();
  for (const run of runs) {
    const d = new Date(run.started_at);
    const y = d.getFullYear();
    const m = d.getMonth();
    const key = `${y}-${String(m + 1).padStart(2, '0')}`;
    if (!byKey.has(key)) {
      byKey.set(key, {
        key,
        label: d.toLocaleDateString('en-CA', { month: 'long', year: 'numeric' }),
        runs: [],
      });
    }
    byKey.get(key).runs.push(run);
  }
  const groups = Array.from(byKey.values());
  for (const g of groups) {
    g.runs.sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
  }
  groups.sort((a, b) => {
    if (a.key > b.key) return -1;
    if (a.key < b.key) return 1;
    return 0;
  });
  return groups;
}

export default function SecurityDashboard() {
  const [runs, setRuns]               = useState([]);
  const [selectedRun, setSelectedRun] = useState(null);
  const [findings, setFindings]       = useState([]);
  const [allFindings, setAllFindings] = useState([]);
  /** Severity totals from API for the selected run (per-run detection; not open backlog). */
  const [apiRunCounts, setApiRunCounts]     = useState(null);
  /** Findings in the selected run covered by an accepted risk (not in the headline). */
  const [apiAcceptedCount, setApiAcceptedCount] = useState(0);
  const [acceptances, setAcceptances] = useState([]);
  const [acceptModal, setAcceptModal] = useState(null); // { advisoryId, findings } | null
  const [loadingRuns, setLoadingRuns] = useState(true);
  const [loadingFindings, setLoadingFindings] = useState(false);
  const [scanning, setScanning]       = useState(false);
  const [scanProgress, setScanProgress] = useState(null);
  const [error, setError]             = useState(null);

  // Filters
  const [agentFilter, setAgentFilter]     = useState('');
  const [severityFilter, setSeverityFilter] = useState('');
  const [statusFilter, setStatusFilter]   = useState('active');

  // Selected finding for detail panel
  const [detailFinding, setDetailFinding] = useState(null);
  const [adminNotes, setAdminNotes]       = useState('');

  // Chat
  const [chatOpen, setChatOpen]   = useState(false);
  const [chatMsgs, setChatMsgs]   = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);
  /** Monotonic guard so slow findings responses cannot overwrite a newer selected scan. */
  const findingsRequestSeqRef = useRef(0);
  /** Bump to re-fetch findings for the current scan (e.g. after on-demand scan). */
  const [findingsRefreshToken, setFindingsRefreshToken] = useState(0);

  // Remediation panel
  const [remediations, setRemediations] = useState([]);

  // Attempts drilldown modal
  const [attemptsModal, setAttemptsModal] = useState(null); // { fingerprint, title } | null
  const [attempts, setAttempts]           = useState([]);
  const [loadingAttempts, setLoadingAttempts] = useState(false);

  // Auto-remediated section collapsed state
  const [autoFixedCollapsed, setAutoFixedCollapsed] = useState(true);

  // Month accordion: collapsed set = month keys user has toggled closed (default: all expanded)
  const [collapsedMonths, setCollapsedMonths] = useState(() => new Set());
  const monthGroups = useMemo(() => groupRunsByMonth(runs), [runs]);

  const toggleMonth = useCallback((monthKey) => {
    setCollapsedMonths((prev) => {
      const next = new Set(prev);
      if (next.has(monthKey)) next.delete(monthKey);
      else next.add(monthKey);
      return next;
    });
  }, []);

  // Keep the month that contains the selected run expanded
  useEffect(() => {
    if (!selectedRun) return;
    const d = new Date(selectedRun.started_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    setCollapsedMonths((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });
  }, [selectedRun?.id, selectedRun?.started_at]);

  // ── Data fetching ──────────────────────────────────────────────────────────

  /**
   * @param {boolean} selectNewest Jump to the newest run. Set after an on-demand
   *   scan: otherwise the report header keeps showing the previous scan's counts
   *   and the new one looks like it never ran.
   */
  const fetchRuns = useCallback(async (selectNewest = false) => {
    setLoadingRuns(true);
    setError(null);
    try {
      const res = await fetch('/api/security/scan-history');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      const runList = data.runs || [];
      setRuns(runList);
      setSelectedRun((prev) => {
        if (runList.length === 0) return prev;
        if (prev == null || selectNewest) return runList[0];
        return prev;
      });
    } catch (err) {
      setError(`OptiGuard unavailable: ${err.message}`);
    }
    setLoadingRuns(false);
  }, []);

  const fetchRemediations = useCallback(async () => {
    if (!selectedRun) return;
    try {
      const res = await fetch(`/api/security/remediation/${selectedRun.id}`);
      if (!res.ok) return;
      const data = await res.json();
      setRemediations(data.remediations || []);
    } catch {
      setRemediations([]);
    }
  }, [selectedRun]);

  const fetchAttempts = useCallback(async (fingerprint) => {
    if (!selectedRun) return;
    setLoadingAttempts(true);
    try {
      const res = await fetch(
        `/api/security/remediation/${selectedRun.id}/attempts?fingerprint=${encodeURIComponent(fingerprint)}`
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setAttempts(data.attempts || []);
    } catch {
      setAttempts([]);
    }
    setLoadingAttempts(false);
  }, [selectedRun]);

  useEffect(() => { fetchRuns(); }, [fetchRuns]);

  useEffect(() => {
    const runId = selectedRun?.id;
    if (runId == null) {
      setAllFindings([]);
      setApiRunCounts(null);
      setLoadingFindings(false);
      return;
    }
    const seq = ++findingsRequestSeqRef.current;
    const ac = new AbortController();
    setLoadingFindings(true);
    setApiRunCounts(null);
    (async () => {
      try {
        const params = new URLSearchParams({ limit: '2000', status: '', scan_run_id: String(runId) });
        const res = await fetch(`/api/security/findings?${params}`, { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (seq !== findingsRequestSeqRef.current) return;
        const rows = data.findings || [];
        setAllFindings(rows);
        setApiAcceptedCount(Number(data.accepted?.total) || 0);
        const norm = normalizeApiCounts(data.counts);
        if (norm) {
          setApiRunCounts(norm);
        } else if (rows.length > 0) {
          const tally = { critical: 0, high: 0, medium: 0, low: 0, info: 0, total: 0 };
          rows.forEach((f) => {
            if (tally[f.severity] !== undefined) tally[f.severity]++;
            tally.total++;
          });
          setApiRunCounts(tally);
        } else {
          setApiRunCounts(null);
        }
      } catch (err) {
        if (err.name === 'AbortError') return;
        console.error('Findings fetch error:', err.message);
      } finally {
        if (seq === findingsRequestSeqRef.current) setLoadingFindings(false);
      }
    })();
    return () => ac.abort();
  }, [selectedRun?.id, findingsRefreshToken]);

  useEffect(() => { fetchRemediations(); }, [fetchRemediations]);

  const fetchAcceptances = useCallback(async () => {
    try {
      const res = await fetch('/api/security/acceptances');
      if (!res.ok) return;
      const data = await res.json();
      setAcceptances(data.acceptances || []);
    } catch {
      setAcceptances([]);
    }
  }, []);

  useEffect(() => { fetchAcceptances(); }, [fetchAcceptances, findingsRefreshToken]);

  async function createAcceptance(body) {
    const res = await fetch('/api/security/acceptances', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    setAcceptModal(null);
    fetchRuns();
    setFindingsRefreshToken((t) => t + 1);
  }

  async function revokeAcceptance(acc) {
    const note = window.prompt(`Revoke accepted risk #${acc.id} (${acc.advisory_id})?\nIts findings reopen on the dashboard. Why?`);
    if (!note) return;
    try {
      const res = await fetch(`/api/security/acceptances/${acc.id}/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
      fetchRuns();
      setFindingsRefreshToken((t) => t + 1);
    } catch (err) {
      alert(`Revoke failed: ${err.message}`);
    }
  }

  // If scan list refreshes and the selected run was removed, select the newest remaining run.
  useEffect(() => {
    if (runs.length === 0) return;
    const sid = selectedRun != null ? Number(selectedRun.id) : NaN;
    if (!selectedRun || !runs.some((r) => Number(r.id) === sid)) {
      setSelectedRun(runs[0]);
    }
  }, [runs, selectedRun]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMsgs]);

  // Apply filters to allFindings.
  // "active" = needs human attention: open, acknowledged, or escalated (not resolved/false_positive).
  useEffect(() => {
    let f = allFindings;
    if (agentFilter)    f = f.filter(x => x.sub_agent === agentFilter);
    if (severityFilter) f = f.filter(x => x.severity === severityFilter);
    if (statusFilter === 'active') {
      f = f.filter(x => x.status === 'open' || x.status === 'acknowledged' || x.status === 'escalated');
    } else if (statusFilter) {
      f = f.filter(x => x.status === statusFilter);
    }
    setFindings(f);
  }, [allFindings, agentFilter, severityFilter, statusFilter]);

  // ── Actions ────────────────────────────────────────────────────────────────

  async function triggerScan() {
    setScanning(true);
    setScanProgress('Scan started — this takes about 45 seconds…');
    try {
      await fetch('/api/security/run', { method: 'POST' });
      let elapsed = 0;
      const interval = setInterval(() => {
        elapsed += 5;
        setScanProgress(`Scanning… ${elapsed}s elapsed`);
        if (elapsed >= 50) {
          clearInterval(interval);
          setScanProgress(null);
          setScanning(false);
          fetchRuns(true);
          setFindingsRefreshToken((t) => t + 1);
        }
      }, 5000);
    } catch (err) {
      setScanProgress(`Error: ${err.message}`);
      setScanning(false);
    }
  }

  async function updateStatus(findingId, status, notes) {
    try {
      const res = await fetch(`/api/security/findings/${findingId}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status, admin_notes: notes }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDetailFinding(null);
      setFindingsRefreshToken((t) => t + 1);
    } catch (err) {
      alert(`Failed to update: ${err.message}`);
    }
  }

  async function sendChat() {
    if (!chatInput.trim()) return;
    const msg = chatInput.trim();
    setChatInput('');
    setChatMsgs(prev => [...prev, { role: 'user', content: msg }]);
    setChatLoading(true);
    try {
      const res = await fetch('/api/security/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: msg }),
      });
      const data = await res.json();
      setChatMsgs(prev => [...prev, { role: 'assistant', content: data.response || data.error || 'No response' }]);
    } catch (err) {
      setChatMsgs(prev => [...prev, { role: 'assistant', content: `Error: ${err.message}` }]);
    }
    setChatLoading(false);
  }

  // ── Computed ───────────────────────────────────────────────────────────────
  const runCounts = selectedRun
    ? (() => {
        const countRows = (rows) => {
          const c = { critical: 0, high: 0, medium: 0, low: 0, info: 0, total: 0 };
          rows.forEach((f) => {
            if (c[f.severity] !== undefined) c[f.severity]++;
            c.total++;
          });
          return c;
        };
        const filtersNarrowList =
          !!agentFilter ||
          !!severityFilter ||
          (statusFilter !== 'active' && statusFilter !== '');
        if (filtersNarrowList) return countRows(findings.filter(f => !isAcceptedInRun(f)));
        if (apiRunCounts) return apiRunCounts;
        const fromRows = countRows(allFindings.filter(f => !isAcceptedInRun(f)));
        if (fromRows.total > 0) return fromRows;
        const fromHistory = detectionCountsFromRunRow(selectedRun);
        if (fromHistory && (fromHistory.total > 0 || fromHistory.critical > 0 || fromHistory.high > 0 || fromHistory.medium > 0 || fromHistory.low > 0 || fromHistory.info > 0)) {
          return fromHistory;
        }
        return fromRows;
      })()
    : null;

  // Overall status is driven by open/ack/escalated backlog for critical+high, plus synthetic signal when
  // detection totals exist but per-run finding rows are not loaded (stale fetch / legacy snapshot).
  const openUrgentCount = allFindings.filter(f =>
    isActiveInRun(f) && ['critical', 'high'].includes(f.severity)
  ).length;
  /** When per-run findings are not loaded yet (or legacy empty snapshot), still reflect scan detection totals. */
  const syntheticUrgent =
    allFindings.length === 0 &&
    runCounts &&
    (runCounts.critical > 0 || runCounts.high > 0);
  const urgentSignal = openUrgentCount > 0 || syntheticUrgent;

  const overallStatus = runCounts
    ? (runCounts.critical > 0 && urgentSignal) ? { label: '⛔ CRITICAL', cls: 'status-critical' }
    : urgentSignal                             ? { label: '⚠️ WARNING',  cls: 'status-warning' }
    : runCounts.medium > 0                     ? { label: '⚠️ NOTICE',   cls: 'status-notice' }
    :                                            { label: '✅ PASS',      cls: 'status-pass' }
    : null;

  const agentKeys = Object.keys(SUB_AGENT_LABELS);

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="sec-dash">
      {/* ── Left: scan history ── */}
      <aside className="sec-sidebar">
        <div className="sec-sidebar-header">
          <span className="sec-sidebar-title">🛡️ OptiGuard</span>
          <button
            className={`sec-run-btn ${scanning ? 'scanning' : ''}`}
            onClick={triggerScan}
            disabled={scanning}
            title="Run security scan now"
          >
            {scanning ? '⏳' : '▶'}
          </button>
        </div>

        {scanProgress && <div className="sec-scan-progress">{scanProgress}</div>}

        {error && <div className="sec-sidebar-error">{error}</div>}

        {loadingRuns ? (
          <div className="sec-sidebar-loading">Loading…</div>
        ) : runs.length === 0 ? (
          <div className="sec-sidebar-empty">No scans yet.<br/>Click ▶ to run first scan.</div>
        ) : (
          <div className="sec-run-list">
            {monthGroups.map((group) => {
              const isOpen = !collapsedMonths.has(group.key);
              return (
                <div key={group.key} className="sec-month-group">
                  <button
                    type="button"
                    className="sec-month-header"
                    onClick={() => toggleMonth(group.key)}
                    aria-expanded={isOpen}
                  >
                    <span className="sec-month-chevron" aria-hidden>{isOpen ? '▼' : '▶'}</span>
                    <span className="sec-month-label">{group.label}</span>
                    <span className="sec-month-badge">{group.runs.length}</span>
                  </button>
                  {isOpen && (
                    <div className="sec-month-runs">
                      {group.runs.map((run) => {
                        const d = new Date(run.started_at);
                        const dateStr = d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric' });
                        const timeStr = d.toLocaleTimeString('en-CA', { hour: '2-digit', minute: '2-digit' });
                        const isSelected = selectedRun?.id === run.id;
                        const dc = detectionCountsFromRunRow(run);
                        const acceptedN = Number(run.accepted_total) || 0;
                        const registerN = (acceptances || [])
                          .filter(a => a.state !== 'revoked' && a.accepted_at && new Date(a.accepted_at) <= new Date(run.started_at))
                          .reduce((n, a) => n + (Number(a.covered_count) || 0), 0);
                        const shownA = acceptedN > 0 ? acceptedN : registerN;
                        const shownAFromRegister = acceptedN === 0 && shownA > 0;
                        const statusCls = dc.critical > 0 ? 'dot-critical'
                                        : dc.high > 0     ? 'dot-high'
                                        : dc.medium > 0   ? 'dot-medium'
                                        : 'dot-pass';

                        return (
                          <button
                            key={run.id}
                            className={`sec-run-item ${isSelected ? 'selected' : ''}`}
                            onClick={() => setSelectedRun(run)}
                          >
                            <span className={`sec-run-dot ${statusCls}`} />
                            <span className="sec-run-date">{dateStr}<span className="sec-run-time">{timeStr}</span></span>
                            <span className="sec-run-counts">
                              {dc.critical > 0 && <span style={{ color: SEVERITY_COLOR.critical }}>{dc.critical}C</span>}
                              {dc.high > 0     && <span style={{ color: SEVERITY_COLOR.high }}>     {dc.high}H</span>}
                              {dc.medium > 0   && <span style={{ color: SEVERITY_COLOR.medium }}>   {dc.medium}M</span>}
                              {shownA > 0 && (
                                <span
                                  style={{ color: ACCEPTED_COLOR }}
                                  title={shownAFromRegister
                                    ? `${shownA} finding(s) stay on the accepted-risk register. This scan did not count them.`
                                    : `${shownA} finding(s) covered by accepted risk`}
                                >
                                  {' '}{shownA}A
                                </span>
                              )}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <button className="sec-chat-toggle" onClick={() => setChatOpen(o => !o)}>
          {chatOpen ? '✕ Close Chat' : '🤖 Security AI Chat'}
        </button>
      </aside>

      {/* ── Right: main content ── */}
      <main className="sec-main">
        {chatOpen ? (
          <SecurityChat
            messages={chatMsgs}
            input={chatInput}
            loading={chatLoading}
            chatEndRef={chatEndRef}
            onInputChange={setChatInput}
            onSend={sendChat}
            onClose={() => setChatOpen(false)}
          />
        ) : detailFinding ? (
          <FindingDetail
            finding={detailFinding}
            adminNotes={adminNotes}
            onNotesChange={setAdminNotes}
            onClose={() => setDetailFinding(null)}
            onStatusUpdate={updateStatus}
            acceptance={acceptances.find(a => a.id === (detailFinding.acceptance_id || detailFinding.raw_details?.accepted_risk_id))}
          />
        ) : selectedRun ? (
          <>
            {/* Email-style report header */}
            <ReportHeader
              run={selectedRun}
              counts={runCounts}
              status={overallStatus}
              acceptedCount={apiAcceptedCount}
              acceptances={acceptances}
            />

            {/* Sub-agent breakdown table: all findings for this run with auto-fixed/escalated breakdown */}
            <SubAgentTable findings={allFindings} agentKeys={agentKeys} />

            {/* Auto-Remediation summary + Escalated split */}
            <FindingsSplit
              allFindings={allFindings}
              remediations={remediations}
              loadingFindings={loadingFindings}
              agentFilter={agentFilter}
              severityFilter={severityFilter}
              statusFilter={statusFilter}
              setAgentFilter={setAgentFilter}
              setSeverityFilter={setSeverityFilter}
              setStatusFilter={setStatusFilter}
              agentKeys={agentKeys}
              autoFixedCollapsed={autoFixedCollapsed}
              setAutoFixedCollapsed={setAutoFixedCollapsed}
              onSelectFinding={(f) => { setDetailFinding(f); setAdminNotes(f.admin_notes || ''); }}
              onViewAttempts={(f) => {
                setAttemptsModal({ fingerprint: f.fingerprint, title: f.title });
                fetchAttempts(f.fingerprint);
              }}
              acceptances={acceptances}
              onAcceptRisk={(advisoryId, rows) => setAcceptModal({ advisoryId, findings: rows })}
              onRevokeAcceptance={revokeAcceptance}
            />

            {acceptModal && (
              <AcceptRiskModal
                advisoryId={acceptModal.advisoryId}
                findings={acceptModal.findings}
                onSubmit={createAcceptance}
                onClose={() => setAcceptModal(null)}
              />
            )}

            {/* Attempts drilldown modal */}
            {attemptsModal && (
              <AttemptsModal
                title={attemptsModal.title}
                attempts={attempts}
                loading={loadingAttempts}
                onClose={() => { setAttemptsModal(null); setAttempts([]); }}
              />
            )}
          </>
        ) : (
          <div className="sec-empty-state">
            <div className="sec-empty-icon">🛡️</div>
            <h2>OptiGuard Security</h2>
            <p>No scan selected. Click a scan from the history list, or run a new scan.</p>
            <button className="sec-run-btn-large" onClick={triggerScan} disabled={scanning}>
              {scanning ? '⏳ Scanning…' : '▶ Run First Scan'}
            </button>
          </div>
        )}
      </main>
    </div>
  );
}

// ── Remediation panel ─────────────────────────────────────────────────────────

function RemediationPanel({ remediations }) {
  const fixed   = remediations.filter(r => r.status === 'resolved');
  const partial = remediations.filter(r => r.status === 'acknowledged');

  if (remediations.length === 0) {
    return (
      <div className="sec-remediation-panel none">
        <span className="sec-rem-icon">🔧</span>
        <span className="sec-rem-none-text">Auto-Remediation — No auto-fixable findings detected in this scan.</span>
      </div>
    );
  }

  return (
    <div className={`sec-remediation-panel ${partial.length > 0 ? 'partial' : 'fixed'}`}>
      <div className="sec-rem-header">
        <span className="sec-rem-icon">🔧</span>
        <h2 className="sec-rem-title">
          Auto-Remediation
          {fixed.length > 0 && (
            <span className="sec-rem-badge fixed">{fixed.length} fixed</span>
          )}
          {partial.length > 0 && (
            <span className="sec-rem-badge partial">{partial.length} partial</span>
          )}
        </h2>
      </div>

      <div className="sec-rem-list">
        {fixed.map(r => (
          <div key={r.id} className="sec-rem-item fixed">
            <span className="sec-rem-status-dot">✅</span>
            <div className="sec-rem-item-body">
              <span className="sec-rem-item-title">{r.title}</span>
              {r.file_path && (
                <code className="sec-rem-item-file">{r.file_path}</code>
              )}
              <span className="sec-rem-item-action">
                {parseRemAction(r.admin_notes)}
              </span>
            </div>
            <span
              className="sec-rem-sev-badge"
              style={{ background: SEVERITY_COLOR[r.severity] || '#64748b' }}
            >
              {r.severity}
            </span>
          </div>
        ))}
        {partial.map(r => (
          <div key={r.id} className="sec-rem-item partial">
            <span className="sec-rem-status-dot">⚠️</span>
            <div className="sec-rem-item-body">
              <span className="sec-rem-item-title">{r.title}</span>
              {r.file_path && (
                <code className="sec-rem-item-file">{r.file_path}</code>
              )}
              <span className="sec-rem-item-action">
                Fix attempted — {parseRemResult(r.admin_notes)} — manual review needed
              </span>
            </div>
            <span
              className="sec-rem-sev-badge"
              style={{ background: SEVERITY_COLOR[r.severity] || '#64748b' }}
            >
              {r.severity}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Extract "Action: ..." line from admin_notes */
function parseRemAction(notes) {
  if (!notes) return '';
  const match = notes.match(/^Action:\s*(.+)$/m);
  return match ? match[1] : '';
}

/** Extract "Result: ..." line from admin_notes */
function parseRemResult(notes) {
  if (!notes) return 'see admin notes';
  const match = notes.match(/^Result:\s*(.+)$/m);
  return match ? match[1] : 'see admin notes';
}

/** Extract OptiGuard AI analysis blurb from admin_notes */
function parseAIBlurb(notes) {
  if (!notes) return null;
  const match = notes.match(/OptiGuard AI analysis:\n([\s\S]+?)(?:\n\nAuto-remediation|$)/);
  return match ? match[1].trim() : null;
}

// ── FindingsSplit: two-section layout (Auto-Remediated + Escalated/Open) ──────

function FindingsSplit({
  allFindings, remediations, loadingFindings,
  agentFilter, severityFilter, statusFilter,
  setAgentFilter, setSeverityFilter, setStatusFilter,
  agentKeys, autoFixedCollapsed, setAutoFixedCollapsed,
  onSelectFinding, onViewAttempts,
  acceptances, onAcceptRisk, onRevokeAcceptance,
}) {
  const [acceptedCollapsed, setAcceptedCollapsed] = useState(false);

  // Partition findings by what the loop did to them
  const autoFixedFindings = allFindings.filter(f =>
    f.status === 'resolved' &&
    (f.admin_notes || '').includes('Auto-remediation by scan #')
  );

  const escalatedAndOpen = allFindings.filter(isActiveInRun);
  const acceptedFindings = allFindings.filter(isAcceptedInRun);

  // Apply user filters to the escalated/open list only (auto-fixed is always shown collapsed)
  let filteredEscalated = escalatedAndOpen;
  if (agentFilter)    filteredEscalated = filteredEscalated.filter(x => x.sub_agent === agentFilter);
  if (severityFilter) filteredEscalated = filteredEscalated.filter(x => x.severity === severityFilter);
  if (statusFilter === 'active') {
    filteredEscalated = filteredEscalated; // already only open/ack/escalated
  } else if (statusFilter) {
    filteredEscalated = filteredEscalated.filter(x => x.status === statusFilter);
  }

  // Remediation attempt metadata keyed by fingerprint
  const remByFp = new Map();
  for (const r of (remediations || [])) {
    if (r.fingerprint) remByFp.set(r.fingerprint, r);
  }

  // Dependency findings share a root advisory across directories; group them so one decision covers all.
  const advisoryGroups = new Map();
  const ungrouped = [];
  for (const f of filteredEscalated) {
    const ids = f.sub_agent === 'dependency_auditor' ? advisoryIdsOf(f) : [];
    if (ids.length === 0) { ungrouped.push(f); continue; }
    const key = ids.join(' + ');
    if (!advisoryGroups.has(key)) advisoryGroups.set(key, { ids, rows: [] });
    advisoryGroups.get(key).rows.push(f);
  }

  const acceptedById = new Map();
  for (const f of acceptedFindings) {
    const id = f.acceptance_id || f.raw_details?.accepted_risk_id;
    if (!acceptedById.has(id)) acceptedById.set(id, []);
    acceptedById.get(id).push(f);
  }
  const shownAcceptances = (acceptances || []).filter(a =>
    a.state !== 'revoked' || acceptedById.has(a.id) ||
    (a.state_changed_at && Date.now() - new Date(a.state_changed_at).getTime() < 30 * 86400000)
  );
  const acceptanceView = shownAcceptances.map(a => {
    const live = acceptedById.get(a.id) || [];
    if (live.length) return { acceptance: a, rows: live, remembered: false };
    const remembered = a.covered_findings || [];
    return { acceptance: a, rows: remembered, remembered: remembered.length > 0 };
  });
  const registerCount = acceptanceView.reduce((n, entry) => n + entry.rows.length, 0);

  return (
    <div className="sec-findings-section">

      {/* Auto-Remediated section */}
      {autoFixedFindings.length > 0 && (
        <div className="sec-autofixed-section">
          <button
            className="sec-autofixed-header"
            onClick={() => setAutoFixedCollapsed(c => !c)}
          >
            <span className="sec-autofixed-icon">🔧</span>
            <span className="sec-autofixed-title">
              Auto-Remediated
              <span className="sec-autofixed-badge">{autoFixedFindings.length} fixed</span>
            </span>
            <span className="sec-autofixed-chevron">{autoFixedCollapsed ? '▶' : '▼'}</span>
          </button>

          {!autoFixedCollapsed && (
            <div className="sec-autofixed-list">
              {autoFixedFindings.map(f => {
                const meta = remByFp.get(f.fingerprint);
                const commitSha   = meta?.commit_sha   || parseCommitFromNotes(f.admin_notes);
                const attemptsRun = meta?.attempts_run || parseAttemptsFromNotes(f.admin_notes);
                const fixedAt     = f.resolved_at ? new Date(f.resolved_at) : null;
                return (
                  <div key={f.fingerprint || f.id} className="sec-autofixed-row">
                    <span className="sec-autofixed-dot">✅</span>
                    <div className="sec-autofixed-body">
                      <span className="sec-autofixed-row-title">{f.title}</span>
                      <span className="sec-autofixed-meta">
                        Detected {formatShortTs(f.first_seen_at)}
                        {fixedAt && <> · Fixed {formatShortTs(fixedAt)}</>}
                        {attemptsRun != null && <> · {attemptsRun} attempt(s)</>}
                        {commitSha && <> · commit <code className="sec-commit-sha">{commitSha}</code></>}
                      </span>
                    </div>
                    <button
                      className="sec-view-attempts-btn"
                      onClick={() => onViewAttempts(f)}
                    >
                      View log
                    </button>
                    <span
                      className="sec-finding-sev-badge"
                      style={{ background: SEVERITY_COLOR[f.severity] || '#64748b' }}
                    >
                      {f.severity}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Escalated / Open / Acknowledged section */}
      <div className="sec-findings-toolbar">
        <h2 className="sec-section-title">
          {escalatedAndOpen.some(f => f.status === 'escalated')
            ? '⚠️ Escalated — Needs Attention'
            : 'Findings'}
          {filteredEscalated.length > 0 && (
            <span className="sec-findings-count" style={{ marginLeft: 10 }}>
              {filteredEscalated.length}
            </span>
          )}
        </h2>
        <div className="sec-filters">
          <select className="sec-filter-select" value={agentFilter} onChange={e => setAgentFilter(e.target.value)}>
            <option value="">All Sub-agents</option>
            {agentKeys.map(k => <option key={k} value={k}>{SUB_AGENT_LABELS[k]}</option>)}
          </select>
          <select className="sec-filter-select" value={severityFilter} onChange={e => setSeverityFilter(e.target.value)}>
            <option value="">All Severities</option>
            {['critical','high','medium','low','info'].map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <select className="sec-filter-select" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
            <option value="active">Active (Open + Escalated)</option>
            <option value="">All Statuses</option>
            {Object.entries(STATUS_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      {loadingFindings ? (
        <div className="sec-loading">Loading findings…</div>
      ) : filteredEscalated.length === 0 ? (
        <div className="sec-empty">
          {acceptedFindings.length > 0
            ? `✅ Nothing requiring attention — ${acceptedFindings.length} finding(s) covered by accepted risk below.`
            : registerCount > 0
            ? `The accepted-risk register below still lists ${registerCount} finding(s). This scan did not report them.`
            : autoFixedFindings.length > 0
            ? '✅ All findings auto-remediated — nothing requiring attention.'
            : '✅ No findings match current filters.'}
        </div>
      ) : (
        <div className="sec-findings-list">
          {[...advisoryGroups.entries()].map(([key, group]) => {
            const dirs = new Set(group.rows.map(f => f.raw_details?.relDir).filter(Boolean));
            const devOnly = group.rows.filter(f => f.raw_details?.dev_only).length;
            const hasSafeFix = group.rows.some(f => f.raw_details?.advisory?.fixAvailable === true);
            return (
              <div key={key} className="sec-advisory-group">
                <div className="sec-advisory-header">
                  <span className="sec-advisory-id">{key}</span>
                  <span className="sec-advisory-meta">
                    {group.rows.length} finding(s) · {dirs.size} director{dirs.size === 1 ? 'y' : 'ies'}
                    {devOnly > 0 && ` · ${devOnly} dev-only`}
                    {hasSafeFix && ' · npm has a non-breaking fix'}
                  </span>
                  {!hasSafeFix && group.ids.map(id => (
                    <button
                      key={id}
                      className="sec-accept-btn"
                      onClick={() => onAcceptRisk(id, allFindings.filter(f => isActiveInRun(f) && advisoryIdsOf(f).includes(id)))}
                      title="Record a reviewed, time-boxed accepted risk for this advisory"
                    >
                      Accept risk{group.ids.length > 1 ? ` (${id})` : ''}…
                    </button>
                  ))}
                </div>
                {group.rows.map(f => (
                  <FindingRow
                    key={f.fingerprint || f.id}
                    finding={f}
                    onSelect={() => onSelectFinding(f)}
                    onViewAttempts={f.status === 'escalated' ? () => onViewAttempts(f) : null}
                  />
                ))}
              </div>
            );
          })}
          {ungrouped.map(f => (
            <FindingRow
              key={f.fingerprint || f.id}
              finding={f}
              onSelect={() => onSelectFinding(f)}
              onViewAttempts={f.status === 'escalated' ? () => onViewAttempts(f) : null}
            />
          ))}
        </div>
      )}

      {/* Accepted risk: reviewed decisions, re-checked every scan, not counted in the headline */}
      {shownAcceptances.length > 0 && (
        <div className="sec-accepted-section">
          <button className="sec-autofixed-header" onClick={() => setAcceptedCollapsed(c => !c)}>
            <span className="sec-autofixed-icon">⏸</span>
            <span className="sec-autofixed-title">
              Accepted Risk
              <span className="sec-accepted-badge">
                {acceptedFindings.length > 0
                  ? `${acceptedFindings.length} finding(s) · not counted above`
                  : `${registerCount} finding(s) on the register · not in this scan`}
              </span>
            </span>
            <span className="sec-autofixed-chevron">{acceptedCollapsed ? '▶' : '▼'}</span>
          </button>

          {!acceptedCollapsed && (
            <div className="sec-accepted-list">
              {acceptanceView.map(({ acceptance: a, rows, remembered }) => {
                const reviewBy = a.review_by ? String(a.review_by).slice(0, 10) : null;
                const daysLeft = reviewBy ? Math.ceil((new Date(reviewBy + 'T23:59:59') - Date.now()) / 86400000) : null;
                return (
                  <div key={a.id} className={`sec-acceptance-card state-${a.state}`}>
                    <div className="sec-acceptance-top">
                      <span className="sec-advisory-id">
                        #{a.id} · {a.advisory_id}{a.cve_id ? ` · ${a.cve_id}` : ''} · {a.package_name}
                      </span>
                      <span className={`sec-acceptance-state state-${a.state}`}>
                        {ACCEPTANCE_STATE_LABELS[a.state] || a.state}
                      </span>
                      {a.state === 'active' && (
                        <button className="sec-act-btn reopen" onClick={() => onRevokeAcceptance(a)}>Revoke</button>
                      )}
                    </div>
                    {a.advisory_title && <div className="sec-acceptance-title">{a.advisory_title}</div>}
                    <div className="sec-acceptance-meta">
                      {REASON_LABELS[a.reason_code] || a.reason_code}
                      {' · '}accepted by {a.accepted_by} on {formatShortTs(a.accepted_at)}
                      {reviewBy && <> · review by <strong>{reviewBy}</strong>{a.state === 'active' && daysLeft != null && ` (${daysLeft}d)`}</>}
                    </div>
                    <div className="sec-acceptance-reason">{a.reason}</div>
                    <div className="sec-acceptance-meta">
                      Scope: {Array.isArray(a.scope_dirs) ? a.scope_dirs.join(', ') : 'any directory'}
                      {a.evidence_url && <> · <a href={a.evidence_url} target="_blank" rel="noreferrer">evidence</a></>}
                      {a.upstream_checked_at && <> · upstream checked {formatShortTs(a.upstream_checked_at)}
                        {a.upstream_patched_version ? ` — patched in ${a.upstream_patched_version}` : ' — still no patch'}</>}
                    </div>
                    {a.state !== 'active' && a.state_reason && (
                      <div className="sec-acceptance-lapsed">{a.state_reason}</div>
                    )}
                    {remembered && (
                      <div className="sec-acceptance-meta">
                        Last recorded in scan #{a.covered_scan_run_id}. This scan did not report them, so the list stays on the register.
                      </div>
                    )}
                    {rows.length > 0 && (
                      <div className="sec-accepted-rows">
                        {rows.map(f => (
                          <FindingRow key={f.fingerprint || f.id} finding={f} onSelect={() => onSelectFinding(f)} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Accept-risk form ──────────────────────────────────────────────────────────

function AcceptRiskModal({ advisoryId, findings, onSubmit, onClose }) {
  const dirs = [...new Set(findings.map(f => f.raw_details?.relDir).filter(Boolean))].sort();
  const devOnlyDirs = new Set(findings.filter(f => f.raw_details?.dev_only).map(f => f.raw_details.relDir));
  const via = findings
    .flatMap(f => (Array.isArray(f.raw_details?.advisory?.via) ? f.raw_details.advisory.via : []))
    .find(v => v && typeof v === 'object' && (v.url || '').toLowerCase().includes(advisoryId.toLowerCase()));

  const [scope, setScope] = useState(new Set(dirs));
  const [reasonCode, setReasonCode] = useState(devOnlyDirs.size === dirs.length ? 'dev_only' : 'no_fix');
  const [reason, setReason] = useState('');
  const [evidenceUrl, setEvidenceUrl] = useState('');
  const [reviewBy, setReviewBy] = useState(addDaysIso(30));
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState(null);

  function toggleDir(d) {
    setScope(prev => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d); else next.add(d);
      return next;
    });
  }

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    setSubmitting(true);
    try {
      await onSubmit({
        advisory_id: advisoryId,
        scope_dirs: [...scope],
        reason_code: reasonCode,
        reason: reason.trim(),
        evidence_url: evidenceUrl.trim() || null,
        review_by: reviewBy,
      });
    } catch (ex) {
      setErr(ex.message);
      setSubmitting(false);
    }
  }

  const reasonOk = reason.trim().length >= 30;

  return (
    <div className="sec-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <form className="sec-modal sec-accept-modal" onSubmit={submit}>
        <div className="sec-modal-header">
          <span className="sec-modal-title">⏸ Accept risk — {advisoryId}</span>
          <button type="button" className="sec-chat-close" onClick={onClose}>✕</button>
        </div>
        {via?.title && <div className="sec-modal-finding-title">{via.title}{via.range ? ` (${via.range})` : ''}</div>}
        <p className="sec-accept-help">
          Accepted findings leave the headline count but stay tracked. Every scan re-checks them and reopens
          them if npm offers a non-breaking fix, GitHub publishes a patched version, upstream raises the
          severity, a new directory is affected, a dev-only package reaches production, or the review date passes.
        </p>

        <label className="meta-label">Scope ({scope.size}/{dirs.length} directories)</label>
        <div className="sec-accept-dirs">
          {dirs.map(d => (
            <label key={d} className="sec-accept-dir">
              <input type="checkbox" checked={scope.has(d)} onChange={() => toggleDir(d)} />
              <code>{d}</code>
              {devOnlyDirs.has(d) && <span className="sec-dev-only-tag">DEV-ONLY</span>}
            </label>
          ))}
        </div>

        <label className="meta-label" htmlFor="acc-reason-code">Reason</label>
        <select id="acc-reason-code" className="sec-filter-select" value={reasonCode} onChange={e => setReasonCode(e.target.value)}>
          {Object.entries(REASON_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>

        <label className="meta-label" htmlFor="acc-reason">Justification (min 30 characters)</label>
        <textarea
          id="acc-reason"
          className="sec-notes-textarea"
          rows={4}
          value={reason}
          onChange={e => setReason(e.target.value)}
          placeholder="Why is this safe to accept for now? e.g. no patched release exists; only reachable from dev tooling; we never pass untrusted input to…"
        />

        <label className="meta-label" htmlFor="acc-evidence">Evidence URL (optional)</label>
        <input
          id="acc-evidence"
          className="sec-accept-input"
          type="url"
          value={evidenceUrl}
          onChange={e => setEvidenceUrl(e.target.value)}
          placeholder="https://github.com/…/issues/…"
        />

        <label className="meta-label" htmlFor="acc-review">Review by (max 90 days)</label>
        <input
          id="acc-review"
          className="sec-accept-input"
          type="date"
          value={reviewBy}
          min={addDaysIso(1)}
          max={addDaysIso(90)}
          onChange={e => setReviewBy(e.target.value)}
        />

        {err && <div className="sec-accept-error">{err}</div>}

        <div className="sec-detail-actions">
          <button type="submit" className="sec-act-btn fp" disabled={submitting || !reasonOk || scope.size === 0}>
            {submitting ? 'Saving…' : `Accept risk for ${scope.size} director${scope.size === 1 ? 'y' : 'ies'}`}
          </button>
          <button type="button" className="sec-act-btn acknowledge" onClick={onClose}>Cancel</button>
        </div>
      </form>
    </div>
  );
}

function parseCommitFromNotes(notes) {
  if (!notes) return null;
  const m = notes.match(/Commit:\s*([a-f0-9]{6,40})/i);
  return m ? m[1] : null;
}

function parseAttemptsFromNotes(notes) {
  if (!notes) return null;
  const m = notes.match(/\((\d+) attempt\(s\)\)/);
  return m ? parseInt(m[1], 10) : null;
}

function formatShortTs(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleString('en-CA', {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZone: 'America/Vancouver',
  });
}

// ── Attempts drilldown modal ───────────────────────────────────────────────────

function AttemptsModal({ title, attempts, loading, onClose }) {
  return (
    <div className="sec-modal-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="sec-modal">
        <div className="sec-modal-header">
          <span className="sec-modal-title">🔧 Remediation Log</span>
          <button className="sec-chat-close" onClick={onClose}>✕</button>
        </div>
        <div className="sec-modal-finding-title">{title}</div>
        {loading ? (
          <div className="sec-loading">Loading attempts…</div>
        ) : attempts.length === 0 ? (
          <div className="sec-empty">No attempt records found. (Pre-migration scan?)</div>
        ) : (
          <div className="sec-modal-attempts">
            {attempts.map((a, i) => (
              <div key={a.id || i} className={`sec-attempt-row ${a.outcome === 'clean' ? 'clean' : a.outcome ? 'stuck' : ''}`}>
                <div className="sec-attempt-header">
                  <span className="sec-attempt-num">Attempt {a.attempt_num}</span>
                  <span className="sec-attempt-dir">{a.rel_dir}</span>
                  <span className={`sec-attempt-outcome ${a.outcome || 'in_progress'}`}>
                    {a.outcome === 'clean' ? '✅ clean' :
                     a.outcome === 'stuck' ? '⚠️ stuck' :
                     a.outcome === 'budget_exhausted' ? '⏱ budget exhausted' :
                     a.outcome === 'commit_failed' ? '❌ commit failed' :
                     a.outcome || 'in progress'}
                  </span>
                  {a.commit_sha && (
                    <code className="sec-commit-sha">{a.commit_sha}</code>
                  )}
                </div>
                <div className="sec-attempt-meta">
                  <span>{formatShortTs(a.started_at)} → {formatShortTs(a.completed_at)}</span>
                  <span>Vulns before: {a.vulns_before ?? '—'} · after: {a.vulns_remaining ?? '—'}</span>
                </div>
                {a.fix_output_excerpt && (
                  <pre className="sec-attempt-output">{a.fix_output_excerpt}</pre>
                )}
                {a.llm_summary && (
                  <div className="sec-attempt-llm">
                    <span className="sec-attempt-llm-label">AI analysis (Qwen 32B)</span>
                    <p>{a.llm_summary}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Report header (email-style) ────────────────────────────────────────────────

function ReportHeader({ run, counts, status, acceptedCount, acceptances }) {
  const active = (acceptances || []).filter(a => a.state === 'active');
  const registerCount = (acceptances || [])
    .filter(a => a.state !== 'revoked')
    .reduce((n, a) => n + (Number(a.covered_count) || 0), 0);
  const nextReview = active.map(a => String(a.review_by || '').slice(0, 10)).filter(Boolean).sort()[0];
  const scanDate = new Date(run.started_at).toLocaleDateString('en-CA', {
    weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
  });
  const scanTime = new Date(run.started_at).toLocaleTimeString('en-CA', {
    hour: '2-digit', minute: '2-digit', timeZoneName: 'short', timeZone: 'America/Vancouver',
  });
  const duration = run.completed_at
    ? Math.round((new Date(run.completed_at) - new Date(run.started_at)) / 1000)
    : null;

  return (
    <div className="sec-report-header">
      <div className="sec-report-header-top">
        <div>
          <h1 className="sec-report-title">🛡️ OptiGuard Security Report</h1>
          <p className="sec-report-meta">
            Scan #{run.id} {' \u00B7 '} {scanDate} at {scanTime}
            {duration != null && ` \u00B7 ${duration}s`}
          </p>
        </div>
        {status && (
          <div className={`sec-status-badge ${status.cls}`}>{status.label}</div>
        )}
      </div>

      {/* Severity summary cards */}
      {counts && (
        <div className="sec-severity-cards">
          {['critical','high','medium','low'].map(sev => (
            <div key={sev} className="sec-severity-card" style={{ borderColor: SEVERITY_COLOR[sev] }}>
              <span className="sec-sev-count" style={{ color: SEVERITY_COLOR[sev] }}>{counts[sev]}</span>
              <span className="sec-sev-label">{sev}</span>
            </div>
          ))}
          <div className="sec-severity-card" style={{ borderColor: '#64748b' }}>
            <span className="sec-sev-count" style={{ color: '#94a3b8' }}>{counts.total}</span>
            <span className="sec-sev-label">total</span>
          </div>
          {(acceptedCount > 0 || registerCount > 0) && (
            <div
              className="sec-severity-card accepted"
              style={{ borderColor: ACCEPTED_COLOR, borderStyle: 'dashed' }}
              title={acceptedCount > 0
                ? 'Covered by a reviewed accepted risk — re-checked every scan, not counted in the totals'
                : 'Accepted-risk register. This scan did not report these findings, so they are not in the totals. The list is kept below.'}
            >
              <span className="sec-sev-count" style={{ color: ACCEPTED_COLOR }}>
                {acceptedCount > 0 ? acceptedCount : registerCount}
              </span>
              <span className="sec-sev-label">
                {acceptedCount > 0 ? 'accepted' : 'on register'}
                {active.length > 0 && <> · {active.length} advisor{active.length === 1 ? 'y' : 'ies'}</>}
                {nextReview && <> · review {nextReview}</>}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Sub-agent summary table ───────────────────────────────────────────────────

function SubAgentTable({ findings, agentKeys }) {
  return (
    <div className="sec-agent-table-wrap">
      <h2 className="sec-section-title">By Sub-Agent</h2>
      <table className="sec-agent-table">
        <thead>
          <tr>
            <th>Sub-Agent</th>
            <th style={{ color: SEVERITY_COLOR.critical }}>Critical</th>
            <th style={{ color: SEVERITY_COLOR.high }}>High</th>
            <th style={{ color: SEVERITY_COLOR.medium }}>Medium</th>
            <th style={{ color: SEVERITY_COLOR.low }}>Low</th>
            <th>Info</th>
          </tr>
        </thead>
        <tbody>
          {agentKeys.map(agent => {
            const af = findings.filter(f => f.sub_agent === agent);
            const detected = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
            const fixed     = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };
            const escalated = { critical: 0, high: 0, medium: 0, low: 0, info: 0 };

            af.forEach(f => {
              const s = f.severity;
              if (detected[s] !== undefined) detected[s]++;
              if (f.status === 'resolved' && (f.admin_notes || '').includes('Auto-remediation')) {
                if (fixed[s] !== undefined) fixed[s]++;
              } else if (f.status === 'escalated') {
                if (escalated[s] !== undefined) escalated[s]++;
              }
            });

            const total = Object.values(detected).reduce((a, b) => a + b, 0);

            function cell(sev) {
              const d = detected[sev] || 0;
              const f = fixed[sev]    || 0;
              const e = escalated[sev]|| 0;
              if (d === 0) return <td key={sev}>—</td>;
              return (
                <td key={sev} style={{ color: SEVERITY_COLOR[sev], fontWeight: 700 }}>
                  {d}
                  {f > 0 && <span className="sec-tbl-badge fixed"> ✅{f}</span>}
                  {e > 0 && <span className="sec-tbl-badge escalated"> ⚠️{e}</span>}
                </td>
              );
            }

            return (
              <tr key={agent}>
                <td>{SUB_AGENT_LABELS[agent] || agent}</td>
                {total === 0 ? (
                  <td colSpan={5} style={{ color: '#16a34a', fontStyle: 'italic' }}>✅ Clean</td>
                ) : (
                  <>
                    {cell('critical')}
                    {cell('high')}
                    {cell('medium')}
                    {cell('low')}
                    {cell('info')}
                  </>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Finding row ───────────────────────────────────────────────────────────────

function FindingRow({ finding, onSelect, onViewAttempts }) {
  const aiBlurb = parseAIBlurb(finding.admin_notes);
  return (
    <div className={`sec-finding-row-wrap ${finding.status === 'escalated' ? 'escalated' : ''}`}>
      <button className="sec-finding-row" onClick={onSelect}>
        <span
          className="sec-finding-sev-badge"
          style={{ background: SEVERITY_COLOR[finding.severity] || '#64748b' }}
        >
          {finding.severity}
        </span>
        {finding.status === 'escalated' && (
          <span className="sec-finding-escalated-tag">ESCALATED</span>
        )}
        {finding.raw_details?.dev_only && (
          <span className="sec-dev-only-tag" title="Not installed with npm install --omit=dev — development tooling only">DEV-ONLY</span>
        )}
        <span className="sec-finding-agent-tag">{SUB_AGENT_LABELS[finding.sub_agent]?.split(' ')[0]}</span>
        <span className="sec-finding-title">{finding.title}</span>
        <span className="sec-finding-status-dot" style={{ color: STATUS_COLORS[finding.status] || '#94a3b8' }}>
          {STATUS_LABELS[finding.status] || finding.status}
        </span>
        {finding.first_seen_at && (
          <span className="sec-finding-age">
            {new Date(finding.first_seen_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })}
          </span>
        )}
      </button>
      {aiBlurb && (
        <div className="sec-finding-ai-blurb">🤖 {aiBlurb}</div>
      )}
      {onViewAttempts && (
        <button className="sec-view-attempts-btn inline" onClick={onViewAttempts}>
          View remediation log
        </button>
      )}
    </div>
  );
}

// ── Finding detail panel ─────────────────────────────────────────────────────

function FindingDetail({ finding, adminNotes, onNotesChange, onClose, onStatusUpdate, acceptance }) {
  return (
    <div className="sec-detail-panel">
      <div className="sec-detail-header">
        <button className="sec-back-btn" onClick={onClose}>← Back to findings</button>
        <span
          className="sec-finding-sev-badge lg"
          style={{ background: SEVERITY_COLOR[finding.severity] || '#64748b' }}
        >
          {finding.severity}
        </span>
        <span className="sec-detail-agent">{SUB_AGENT_LABELS[finding.sub_agent] || finding.sub_agent}</span>
      </div>

      <h2 className="sec-detail-title">{finding.title}</h2>

      <div className="sec-detail-meta-grid">
        <div><span className="meta-label">Category</span><span className="meta-value">{finding.category}</span></div>
        <div><span className="meta-label">Status</span><span className="meta-value" style={{ color: STATUS_COLORS[finding.status] }}>{STATUS_LABELS[finding.status]}</span></div>
        {finding.file_path && <div><span className="meta-label">File</span><code className="meta-value code">{finding.file_path}{finding.line_number ? ':' + finding.line_number : ''}</code></div>}
        <div><span className="meta-label">First seen</span><span className="meta-value">{new Date(finding.first_seen_at).toLocaleDateString('en-CA')}</span></div>
        <div><span className="meta-label">Last seen</span><span className="meta-value">{new Date(finding.last_seen_at).toLocaleDateString('en-CA')}</span></div>
      </div>

      {finding.status === 'accepted_risk' && (
        <div className="sec-acceptance-lapsed" style={{ borderColor: ACCEPTED_COLOR, color: '#cbd5e1' }}>
          {acceptance && acceptance.state !== 'active' ? (
            <>
              Accepted risk #{acceptance.id} ({acceptance.advisory_id}) is {ACCEPTANCE_STATE_LABELS[acceptance.state] || acceptance.state}.
              {acceptance.state_reason ? ` ${acceptance.state_reason}` : ''} The finding stays on the register.
            </>
          ) : (
            <>
              ⏸ Covered by accepted risk
              {acceptance
                ? <> #{acceptance.id} ({acceptance.advisory_id}) — review by {String(acceptance.review_by).slice(0, 10)}. {acceptance.reason}</>
                : '.'}
              {' '}Revoke it from the Accepted Risk section to reopen every finding it covers.
            </>
          )}
        </div>
      )}

      <div className="sec-detail-desc-wrap">
        <span className="meta-label">Description</span>
        <pre className="sec-detail-desc">{finding.description}</pre>
      </div>

      <div className="sec-detail-notes-wrap">
        <label className="meta-label" htmlFor="admin-notes">Admin Notes</label>
        <textarea
          id="admin-notes"
          className="sec-notes-textarea"
          value={adminNotes}
          onChange={e => onNotesChange(e.target.value)}
          placeholder="Add notes about this finding…"
          rows={3}
        />
      </div>

      {finding.status !== 'accepted_risk' && <div className="sec-detail-actions">
        {finding.status !== 'acknowledged' && (
          <button className="sec-act-btn acknowledge" onClick={() => onStatusUpdate(finding.id, 'acknowledged', adminNotes)}>
            Acknowledge
          </button>
        )}
        {finding.status !== 'resolved' && (
          <button className="sec-act-btn resolve" onClick={() => onStatusUpdate(finding.id, 'resolved', adminNotes)}>
            Mark Resolved
          </button>
        )}
        {finding.status !== 'false_positive' && (
          <button className="sec-act-btn fp" onClick={() => onStatusUpdate(finding.id, 'false_positive', adminNotes)}>
            False Positive
          </button>
        )}
        {finding.status !== 'open' && (
          <button className="sec-act-btn reopen" onClick={() => onStatusUpdate(finding.id, 'open', adminNotes)}>
            Reopen
          </button>
        )}
      </div>}
    </div>
  );
}

// ── Security Chat ─────────────────────────────────────────────────────────────

function SecurityChat({ messages, input, loading, chatEndRef, onInputChange, onSend, onClose }) {
  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); }
  }

  return (
    <div className="sec-chat-panel">
      <div className="sec-chat-header">
        <span>🤖 Security Coordinator — Qwen 32B</span>
        <button className="sec-chat-close" onClick={onClose}>✕</button>
      </div>
      <div className="sec-chat-hint-strip">
        Ask: "What are the most urgent issues?" · "How do I fix plain text passwords?" · "Summarize last week's auth events"
      </div>
      <div className="sec-chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={`sec-chat-msg ${m.role}`}>
            <span className="sec-chat-role">{m.role === 'user' ? 'You' : 'Security AI'}</span>
            <pre className="sec-chat-content">{m.content}</pre>
          </div>
        ))}
        {loading && (
          <div className="sec-chat-msg assistant">
            <span className="sec-chat-role">Security AI</span>
            <pre className="sec-chat-content sec-chat-thinking">Analyzing findings…</pre>
          </div>
        )}
        <div ref={chatEndRef} />
      </div>
      <div className="sec-chat-input-row">
        <textarea
          className="sec-chat-input"
          value={input}
          onChange={e => onInputChange(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Ask the Security Coordinator…"
          rows={2}
          disabled={loading}
        />
        <button className="sec-chat-send" onClick={onSend} disabled={loading || !input.trim()}>↑</button>
      </div>
    </div>
  );
}
